import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  analyticsService,
  programService,
  type ComplianceOverview,
  type ClientCompliance,
  type ProgramCompliance,
  type ComplianceStatus,
} from '../../services'
import { Button, LoadingScreen } from '../../components/common'

type ViewMode = 'clients' | 'programs'
type StatusFilter = 'all' | ComplianceStatus

export function CompliancePage() {
  const { profile } = useAuthStore()
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>('clients')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [programFilter, setProgramFilter] = useState<string>('all')

  const [overview, setOverview] = useState<ComplianceOverview | null>(null)
  const [clients, setClients] = useState<ClientCompliance[]>([])
  const [programs, setPrograms] = useState<ProgramCompliance[]>([])
  const [availablePrograms, setAvailablePrograms] = useState<{ id: string; title: string }[]>([])

  const hasPaidTier = profile?.subscription_tier && profile.subscription_tier !== 'free'

  useEffect(() => {
    const loadCompliance = async () => {
      if (!profile || !hasPaidTier) {
        setIsLoading(false)
        return
      }

      try {
        const [overviewData, clientData, programData, programsList] = await Promise.all([
          analyticsService.getComplianceOverview(profile.id),
          analyticsService.getClientCompliance(profile.id),
          analyticsService.getProgramCompliance(profile.id),
          programService.getPrograms(profile.id),
        ])

        setOverview(overviewData)
        setClients(clientData)
        setPrograms(programData)
        setAvailablePrograms(programsList.map(p => ({ id: p.id, title: p.title })))
      } catch (error) {
        console.error('Failed to load compliance data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadCompliance()
  }, [profile, hasPaidTier])

  // Filter clients based on selected filters
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false
      if (programFilter !== 'all' && c.programId !== programFilter) return false
      return true
    })
  }, [clients, statusFilter, programFilter])

  if (!hasPaidTier) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Compliance Tracking Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to track client compliance and engagement.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingScreen message="Loading compliance data..." />
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Dashboard</h1>
          <p className="mt-1 text-gray-600">
            Track client progress and identify who needs attention.
          </p>
        </div>

        {/* View mode toggle */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setViewMode('clients')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'clients'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            By Client
          </button>
          <button
            onClick={() => setViewMode('programs')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'programs'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            By Program
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Overall Compliance"
          value={`${overview?.overallComplianceRate || 0}%`}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
        <StatCard
          label="Active Clients"
          value={overview?.totalActiveClients || 0}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        <StatCard
          label="At Risk"
          value={overview?.atRiskCount || 0}
          color="red"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
        <StatCard
          label="On Track"
          value={overview?.onTrackCount || 0}
          color="emerald"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Status breakdown bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Status Breakdown</h3>
        <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
          {overview && overview.totalActiveAssignments > 0 && (
            <>
              <div
                className="bg-emerald-500 transition-all"
                style={{ width: `${(overview.onTrackCount / overview.totalActiveAssignments) * 100}%` }}
                title={`On Track: ${overview.onTrackCount}`}
              />
              <div
                className="bg-yellow-500 transition-all"
                style={{ width: `${(overview.behindCount / overview.totalActiveAssignments) * 100}%` }}
                title={`Behind: ${overview.behindCount}`}
              />
              <div
                className="bg-red-500 transition-all"
                style={{ width: `${(overview.atRiskCount / overview.totalActiveAssignments) * 100}%` }}
                title={`At Risk: ${overview.atRiskCount}`}
              />
              <div
                className="bg-blue-500 transition-all"
                style={{ width: `${(overview.completedCount / overview.totalActiveAssignments) * 100}%` }}
                title={`Completed: ${overview.completedCount}`}
              />
            </>
          )}
        </div>
        <div className="flex justify-between mt-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full" />
            <span className="text-gray-600">On Track ({overview?.onTrackCount || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full" />
            <span className="text-gray-600">Behind ({overview?.behindCount || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-gray-600">At Risk ({overview?.atRiskCount || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-gray-600">Completed ({overview?.completedCount || 0})</span>
          </div>
        </div>
      </div>

      {/* Clients View */}
      {viewMode === 'clients' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="font-semibold text-gray-900">Clients</h3>
            <div className="flex gap-3">
              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="on_track">On Track</option>
                <option value="behind">Behind</option>
                <option value="at_risk">At Risk</option>
                <option value="completed">Completed</option>
              </select>

              {/* Program filter */}
              <select
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Programs</option>
                {availablePrograms.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredClients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {clients.length === 0
                ? 'No active assignments found'
                : 'No clients match the selected filters'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredClients.map((client) => (
                <ClientRow key={client.assignmentId} client={client} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Programs View */}
      {viewMode === 'programs' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Programs</h3>
          </div>

          {programs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No active program assignments found
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {programs.map((program) => (
                <ProgramRow key={program.programId} program={program} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================
// Sub-components
// ============================================

interface StatCardProps {
  label: string
  value: number | string
  color: 'blue' | 'green' | 'red' | 'emerald' | 'yellow'
  icon: React.ReactNode
}

function StatCard({ label, value, color, icon }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
      <div className="flex items-center gap-3 lg:gap-4">
        <div className={`p-2 lg:p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-xl lg:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs lg:text-sm text-gray-600">{label}</p>
        </div>
      </div>
    </div>
  )
}

interface ClientRowProps {
  client: ClientCompliance
}

function ClientRow({ client }: ClientRowProps) {
  const statusConfig = {
    on_track: { label: 'On Track', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
    behind: { label: 'Behind', color: 'bg-yellow-100 text-yellow-700', dotColor: 'bg-yellow-500' },
    at_risk: { label: 'At Risk', color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' },
    completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  }

  const status = statusConfig[client.status]

  const lastActivityLabel = client.lastActivity
    ? formatRelativeTime(client.lastActivity)
    : 'No activity'

  return (
    <Link
      to={`/dashboard/clients/${client.clientId}`}
      className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
    >
      {/* Status indicator */}
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${status.dotColor}`} />

      {/* Avatar */}
      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-medium text-gray-600">
          {client.clientName[0]?.toUpperCase() || '?'}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{client.clientName}</p>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
            {status.label}
          </span>
        </div>
        <p className="text-sm text-gray-500 truncate">
          {client.programTitle} &middot; Day {client.currentDay}
        </p>
      </div>

      {/* Progress */}
      <div className="w-32 hidden sm:block">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">{client.progressPercent}%</span>
          <span className="text-gray-400">{client.completedTasks}/{client.totalTasks}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${status.dotColor}`}
            style={{ width: `${client.progressPercent}%` }}
          />
        </div>
      </div>

      {/* Last activity */}
      <div className="text-sm text-gray-500 text-right hidden md:block w-24">
        {lastActivityLabel}
      </div>

      {/* Arrow */}
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

interface ProgramRowProps {
  program: ProgramCompliance
}

function ProgramRow({ program }: ProgramRowProps) {
  const total = program.totalClients
  const onTrackPercent = total > 0 ? (program.onTrackCount / total) * 100 : 0
  const behindPercent = total > 0 ? (program.behindCount / total) * 100 : 0
  const atRiskPercent = total > 0 ? (program.atRiskCount / total) * 100 : 0

  return (
    <Link
      to={`/dashboard/programs/${program.programId}`}
      className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
    >
      {/* Icon */}
      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{program.programTitle}</p>
        <p className="text-sm text-gray-500">
          {program.totalClients} {program.totalClients === 1 ? 'client' : 'clients'}
        </p>
      </div>

      {/* Compliance bar */}
      <div className="w-40 hidden sm:block">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">{program.avgComplianceRate}% avg</span>
        </div>
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
          <div className="bg-emerald-500" style={{ width: `${onTrackPercent}%` }} />
          <div className="bg-yellow-500" style={{ width: `${behindPercent}%` }} />
          <div className="bg-red-500" style={{ width: `${atRiskPercent}%` }} />
        </div>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-emerald-500 rounded-full" />
          <span className="text-gray-600">{program.onTrackCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-yellow-500 rounded-full" />
          <span className="text-gray-600">{program.behindCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          <span className="text-gray-600">{program.atRiskCount}</span>
        </div>
      </div>

      {/* Arrow */}
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

// ============================================
// Utility functions
// ============================================

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
